import { Component, computed, inject, output } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';

import { StyleClassModule } from 'primeng/styleclass';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AvatarModule } from 'primeng/avatar';
import { Origin8Icon } from '@origin8-web/core-ui/icon';
import { IconComponent } from '@origin8-web/core-ui/icon';
import { filter, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrganizationContextService } from '../../core/organization-context.service';
import { getOrganizationFromRouter } from '../../core/utils/organization-router.util';
import { UserStore } from '../../core/store/user/user.store';
import { ClientOrganization, CLIENT_ORGANIZATIONS } from '@origin8-web/models/core-business';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'topbar',
  standalone: true,
  template: `<div class="layout-topbar">
    <button
      class="text-zinc-500 hover:text-zinc-700 transition-colors cursor-pointer"
      (click)="sidebarToggle.emit()"
      aria-label="Toggle sidebar"
    >
      <o8-icon [icon]="Origin8Icon.MENU" style="font-size: 1.25rem"></o8-icon>
    </button>
    @if (!isDashboard()) {
      <a tabindex="0" type="button" class="menu-button" (click)="goBack()">
        <o8-icon [icon]="Origin8Icon.CLOSE_MENU"></o8-icon>
      </a>
    }
    <h1 class="viewname">{{ pageTitle() }}</h1>
    @if (userStore.isSupervisrAdmin()) {
      <p-select
        [options]="CLIENT_ORGANIZATIONS"
        [ngModel]="selectedOrganization()"
        (ngModelChange)="onOrganizationChange($event)"
        [checkmark]="true"
        optionLabel="displayName"
        class="w-full md:w-56"
      />
    }
    <div class="ml-auto flex items-center gap-2">
      @if (!userStore.isSupervisrReadOnly()) {
        <p-button
          label="Ask Co-Visr"
          severity="info"
          icon="pi pi-comments"
          [text]="true"
          (click)="coVisrClick.emit()"
        />
      }
      @if (!isDashboard()) {
        <o8-icon
          class="cursor-pointer font-bold hover:text-zinc-700"
          [icon]="Origin8Icon.HOME"
          style="font-size: 1.25rem"
          routerLink="/"
        ></o8-icon>
      }
    </div>
  </div>`,
  imports: [
    RouterModule,
    FormsModule,
    StyleClassModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    RippleModule,
    BadgeModule,
    OverlayBadgeModule,
    AvatarModule,
    IconComponent,
    SelectModule,
  ],
})
export class Topbar {
  readonly userStore = inject(UserStore);
  readonly organizationContext = inject(OrganizationContextService);
  readonly router = inject(Router);
  readonly #location = inject(Location);
  readonly Origin8Icon = Origin8Icon;
  readonly sidebarToggle = output<void>();
  readonly coVisrClick = output<void>();

  goBack() {
    this.#location.back();
  }
  readonly CLIENT_ORGANIZATIONS = CLIENT_ORGANIZATIONS;
  readonly pageTitle = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.getPageTitleFromRoute()),
    ),
    { initialValue: this.getPageTitleFromRoute() },
  );
  readonly organization = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => getOrganizationFromRouter(this.router)),
    ),
    { initialValue: getOrganizationFromRouter(this.router) },
  );

  readonly selectedOrganization = computed(() => {
    const orgSlug = this.organization();
    return orgSlug ? (CLIENT_ORGANIZATIONS.find((o) => o.name === orgSlug) ?? null) : null;
  });

  readonly isDashboard = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url.startsWith('/dashboard')),
    ),
    { initialValue: this.router.url.startsWith('/dashboard') },
  );

  onOrganizationChange(event: ClientOrganization) {
    this.organizationContext.switchOrganization(event);
  }

  private getPageTitleFromRoute(): string | undefined {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.data['pageTitle'] as string | undefined;
  }
}
